import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { GridsterConfig, GridsterItem, DisplayGrid, GridType } from 'angular-gridster2';
import { forkJoin } from 'rxjs';
import { HttpDashboardService } from '../../../service/http-dashboard.service';
import { AgentDto, DashboardV2ComponentDto, DashboardV2Dto } from '../../../service/dto';

type Breakpoint = 'XS' | 'SM' | 'MD' | 'LG' | 'XL' | 'XXL';

interface EditorPlacement {
	id: number | null;  // null = new, existing id = update
	component: DashboardV2ComponentDto;
	item: GridsterItem;
	enabled: boolean;
}

@Component({
	selector: 'app-dashboard-editor',
	standalone: false,
	templateUrl: './dashboard-editor.component.html',
	styleUrl: './dashboard-editor.component.scss'
})
export class DashboardEditorComponent implements OnInit {

	dashboardId!: number;
	dashboard: DashboardV2Dto | null = null;
	allComponents: DashboardV2ComponentDto[] = [];

	readonly breakpoints: Breakpoint[] = ['XS', 'SM', 'MD', 'LG', 'XL', 'XXL'];
	readonly breakpointOrder: Breakpoint[] = ['XS', 'SM', 'MD', 'LG', 'XL', 'XXL'];
	activeBreakpoint: Breakpoint = 'LG';

	allPlacements: Map<Breakpoint, EditorPlacement[]> = new Map();
	activePlacements: EditorPlacement[] = [];
	palette: DashboardV2ComponentDto[] = [];

	saving = false;
	loading = true;

	options: GridsterConfig = {
		gridType: GridType.ScrollVertical,
		displayGrid: DisplayGrid.Always,
		cols: 14,
		rowHeight: window.innerHeight * 0.25,
		draggable: { enabled: true },
		resizable: { enabled: true },
		pushItems: false,
		swap: false,
		minCols: 14,
		maxCols: 14,
		minRows: 4,
		margin: 6,
		outerMargin: true,
		itemChangeCallback: () => this.onItemChanged(),
	};

	constructor(
		private route: ActivatedRoute,
		private httpService: HttpDashboardService
	) { }

	ngOnInit(): void {
		this.dashboardId = Number(this.route.snapshot.paramMap.get('id'));

		forkJoin({
			dashboard: this.httpService.getDashboard(this.dashboardId),
			components: this.httpService.getComponents()
		}).subscribe(({ dashboard, components }) => {
			this.dashboard = dashboard;
			this.allComponents = components;

			this.breakpoints.forEach(bp => this.allPlacements.set(bp, []));

			// Load existing placements from dashboard into map
			dashboard.placements?.forEach(p => {
				const bp = p.breakpoint as Breakpoint;
				const list = this.allPlacements.get(bp) ?? [];
				list.push({
					id: p.id,
					component: p.dashboardComponent,
					item: this.toGridsterItem(p),
					enabled: p.enabled
				});
				this.allPlacements.set(bp, list);
			});

			this.loading = false;
			this.switchBreakpoint(this.activeBreakpoint);
		});
	}

	switchBreakpoint(bp: Breakpoint): void {
		this.activeBreakpoint = bp;
		const existing = this.allPlacements.get(bp) ?? [];

		if (existing.length > 0) {
			this.activePlacements = existing;
		} else {
			// Inherit from next smaller breakpoint, strip ids so they save as new
			this.activePlacements = this.inheritFromSmaller(bp);
			this.allPlacements.set(bp, this.activePlacements);
		}

		this.refreshPalette();
	}

	addToGrid(component: DashboardV2ComponentDto): void {
		const placement: EditorPlacement = {
			id: null,
			component,
			enabled: true,
			item: { x: 0, y: this.nextAvailableRow(), cols: 4, rows: 2 }
		};
		this.activePlacements.push(placement);
		this.allPlacements.set(this.activeBreakpoint, this.activePlacements);
		this.refreshPalette();
	}

	removeFromGrid(placement: EditorPlacement): void {
		this.activePlacements = this.activePlacements.filter(p => p !== placement);
		this.allPlacements.set(this.activeBreakpoint, this.activePlacements);
		this.refreshPalette();
	}

	save(): void {
		if (!this.dashboard) return;
		this.saving = true;

		const payload:DashboardV2Dto = {
			id: this.dashboard.id,
			name: this.dashboard.name,
			description: this.dashboard.description,
			main: this.dashboard.main,
			enabled: this.dashboard.enabled,
			placements: this.buildPlacementsPayload(),
			createdAt: new Date(),
			agent: undefined as any as AgentDto,
			hashCode:0
		};

		this.httpService.editDashboard(this.dashboardId, payload).subscribe({
			next: (updated) => {
				this.dashboard = updated;
				this.saving = false;
			},
			error: () => this.saving = false
		});
	}

	private buildPlacementsPayload() {
		const result: any[] = [];
		this.allPlacements.forEach((placements, breakpoint) => {
			placements.forEach(p => {
				result.push({
					id: p.id,       // null = new, existing = update
					breakpoint,
					xStart: p.item.x + 1,
					xEnd: p.item.x + p.item.cols,
					yStart: p.item.y + 1,
					yEnd: p.item.y + p.item.rows,
					enabled: p.enabled,
					dashboardComponent: { id: p.component.id }
				});
			});
		});
		return result;
	}

	private inheritFromSmaller(bp: Breakpoint): EditorPlacement[] {
		const currentIndex = this.breakpointOrder.indexOf(bp);
		for (let i = currentIndex - 1; i >= 0; i--) {
			const smaller = this.allPlacements.get(this.breakpointOrder[i]) ?? [];
			if (smaller.length > 0) {
				// Deep copy, strip ids — these will be new placements for this breakpoint
				return smaller.map(p => ({
					id: null,
					component: p.component,
					enabled: p.enabled,
					item: { ...p.item }
				}));
			}
		}
		return [];
	}

	private onItemChanged(): void {
		this.allPlacements.set(this.activeBreakpoint, this.activePlacements);
	}

	private nextAvailableRow(): number {
		if (this.activePlacements.length === 0) return 0;
		return Math.max(...this.activePlacements.map(p => p.item.y + p.item.rows));
	}

	private toGridsterItem(p: any): GridsterItem {
		return {
			x: p.xStart - 1,
			y: p.yStart - 1,
			cols: p.xEnd - p.xStart + 1,
			rows: p.yEnd - p.yStart + 1,
		};
	}

	private refreshPalette(): void {
		const placedIds = new Set(this.activePlacements.map(p => p.component.id));
		this.palette = this.allComponents.filter(c => !placedIds.has(c.id));
	}
}
